import { computed, inject, Injectable } from '@angular/core';
import { LpRidLocation, LpRidLocationStop, LpRidProvince } from '../../../typings/schema';
import { IRidCodeLeerplusSaveState, LEERPLUS } from '../rid-game.utils';
import { RidHomeworkService } from './rid-homework.service';

/** The player works through one province at a time, and one location of it at a time. While nothing is chosen (or the choice is explored: all its stops are started), the player chooses, but only on a day a new stop can still be started. Everything that isn't chosen or started is locked. Started stops stay open until all their assignments are finished, so the player moves on without having to finish them first. */
@Injectable()
export class RidJourneyService {
  private readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $ridHomeworkService = inject(RidHomeworkService);

  /** The chosen province, until it's explored. */
  public readonly chosenProvince = computed<LpRidProvince | undefined>(() => {
    const uid = this.$lpApiService.state().journey.provinceUid;
    const province = this.$lpApiService.getInstancesByType('RidProvince').find((p) => p.uid === uid);
    return province && !this.isProvinceExplored(province) ? province : undefined;
  });

  /** The chosen location, until it's explored. It's always in the chosen province. */
  public readonly chosenLocation = computed<LpRidLocation | undefined>(() => {
    const uid = this.$lpApiService.state().journey.locationUid;
    const location = this.chosenProvince()?.locations.find((l) => l.uid === uid);
    return location && !this.isLocationExplored(location) ? location : undefined;
  });

  /** Whether the player chooses a province now, so the unexplored ones are all open to choose from. */
  public readonly isChoosingProvince = computed(() => !this.chosenProvince() && this.$ridHomeworkService.canStartNewStop());

  /** Whether the player chooses a location of the chosen province now, so its locations are all open to choose from. */
  public readonly isChoosingLocation = computed(
    () => !!this.chosenProvince() && !this.chosenLocation() && this.$ridHomeworkService.canStartNewStop(),
  );

  public isStopClickable(stop: LpRidLocationStop): boolean {
    // a started stop stays open, to play the assignments left or to look back on them
    if (this.$ridHomeworkService.isStopStarted(stop)) return true;

    // a new stop can only be chosen in the chosen location, and only one a day
    return !!this.chosenLocation()?.stops.some((s) => s.uid === stop.uid) && this.$ridHomeworkService.canStartNewStop();
  }

  public isProvinceLocked(province: LpRidProvince): boolean {
    const isStarted = province.locations.some((location) => this.isLocationStarted(location));
    if (isStarted || this.chosenProvince()?.uid === province.uid) return false;

    return !this.isChoosingProvince();
  }

  public isLocationLocked(location: LpRidLocation): boolean {
    if (this.chosenLocation()?.uid === location.uid || this.isLocationStarted(location)) return false;

    const isOpen = this.isChoosingLocation() && !!this.chosenProvince()?.locations.some((l) => l.uid === location.uid);
    return !isOpen;
  }

  // Choosing is all a click does: it unlocks the province or location, and only the next click opens it. Each returns whether it chose.

  public chooseProvince(province: LpRidProvince): boolean {
    if (!this.isChoosingProvince() || this.isProvinceLocked(province) || this.isProvinceExplored(province)) return false;

    this.setJourney({ provinceUid: province.uid, locationUid: null });
    return true;
  }

  public chooseLocation(location: LpRidLocation): boolean {
    if (this.chosenLocation() || this.isLocationLocked(location) || this.isLocationExplored(location)) return false;

    this.setJourney({ ...this.$lpApiService.state().journey, locationUid: location.uid });
    return true;
  }

  /** There's homework left there for today: a new stop can still be started in it today, or the stop started in it today isn't finished. */
  public hasProvinceWorkToday(province: LpRidProvince): boolean {
    if (this.chosenProvince()?.uid === province.uid && this.$ridHomeworkService.canStartNewStop()) return true;

    return province.locations.some((location) => this.hasUnfinishedStopToday(location));
  }

  /** There's homework left there for today: a new stop can still be started in it today, or the stop started in it today isn't finished. */
  public hasLocationWorkToday(location: LpRidLocation): boolean {
    if (this.chosenLocation()?.uid === location.uid && this.$ridHomeworkService.canStartNewStop()) return true;

    return this.hasUnfinishedStopToday(location);
  }

  private hasUnfinishedStopToday(location: LpRidLocation): boolean {
    return location.stops.some((stop) => this.$ridHomeworkService.isStopToDoToday(stop));
  }

  /** All the stops of its locations are started. */
  public isProvinceExplored(province: LpRidProvince): boolean {
    return province.locations.every((location) => this.isLocationExplored(location));
  }

  private setJourney(journey: IRidCodeLeerplusSaveState['journey']) {
    this.$lpApiService.state.update((s) => ({ ...s, journey }));
  }

  /** At least one of its stops is started. */
  public isLocationStarted(location: LpRidLocation): boolean {
    return location.stops.some((stop) => this.$ridHomeworkService.isStopStarted(stop));
  }

  /** All its stops are started, so the player moves on to another location. */
  public isLocationExplored(location: LpRidLocation): boolean {
    return location.stops.every((stop) => this.$ridHomeworkService.isStopStarted(stop));
  }

  /** All the assignments of all its stops are finished. */
  public isLocationFinished(location: LpRidLocation): boolean {
    return location.stops.every((stop) => this.$ridHomeworkService.isStopFinished(stop));
  }

  public isLocationInProgress(location: LpRidLocation): boolean {
    return this.isLocationStarted(location) && !this.isLocationFinished(location);
  }

  /** The locations sorted in the order their first stop was started, the ones not started yet last, so progress fills up from the left. */
  public sortLocationsByStartOrder(locations: LpRidLocation[]): LpRidLocation[] {
    const earliestPosition = (location: LpRidLocation) =>
      Math.min(...location.stops.map((stop) => this.$ridHomeworkService.positionInStartOrder(stop)));
    return [...locations].sort((a, b) => earliestPosition(a) - earliestPosition(b));
  }
}
